import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { type SandboxClassAsset } from '../../utils/sandboxCourseApi';
import {
    addSandboxClassAsset,
    deleteSandboxClassAsset,
    fetchSandboxClassAssets,
    updateSandboxClassAsset,
    uploadSandboxClassAssetZip,
} from '../../redux/sandbox/actions';
import { isSandboxClassAssetZip, SANDBOX_CLASS_ASSET_MAX_BYTES } from '../../utils/uploadSandboxClassAsset';

type Props = {
    open: boolean;
    courseId: string;
    courseTitle?: string;
    onClose: () => void;
};

function formatSize(bytes?: number) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: OBSIDIAN.surfaceContainerHigh,
        borderRadius: 2,
        color: OBSIDIAN.onSurface,
        fontFamily: OBSIDIAN_BODY_FONT,
        '& fieldset': { borderColor: `${OBSIDIAN.outline}55` },
        '&:hover fieldset': { borderColor: `${OBSIDIAN.outline}99` },
        '&.Mui-focused fieldset': { borderColor: OBSIDIAN.primary },
    },
    '& .MuiInputBase-input': { color: OBSIDIAN.onSurface },
    '& .MuiInputLabel-root': { color: OBSIDIAN.onSurfaceVariant },
};

/**
 * Teacher: upload multiple ZIP class assets (≤20MB) with name + description.
 */
export function SandboxManageClassAssetsModal({ open, courseId, courseTitle, onClose }: Props) {
    const dispatchPromise = useDispatchPromise();
    const inputRef = useRef<HTMLInputElement>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [assets, setAssets] = useState<SandboxClassAsset[]>([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const load = useCallback(async () => {
        if (!courseId) return;
        setLoading(true);
        try {
            const res: any = await dispatchPromise(fetchSandboxClassAssets(courseId));
            setAssets(res?.assets || []);
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load assets', 'error');
            setAssets([]);
        } finally {
            setLoading(false);
        }
    }, [courseId, dispatchPromise]);

    useEffect(() => {
        if (open) {
            setName('');
            setDescription('');
            setPendingFile(null);
            void load();
        }
    }, [open, load]);

    const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!isSandboxClassAssetZip(file)) {
            showToastMessage('Only ZIP files are supported', 'warning');
            return;
        }
        if (file.size > SANDBOX_CLASS_ASSET_MAX_BYTES) {
            showToastMessage('ZIP must be 20MB or smaller', 'warning');
            return;
        }
        setPendingFile(file);
        if (!name.trim()) {
            setName(file.name.replace(/\.zip$/i, '').slice(0, 100));
        }
    };

    const upload = async () => {
        if (!pendingFile) {
            showToastMessage('Choose a ZIP file first', 'info');
            return;
        }
        if (!name.trim()) {
            showToastMessage('Add a name for this asset', 'info');
            return;
        }
        setBusy(true);
        try {
            const file_key = (await dispatchPromise(
                uploadSandboxClassAssetZip({ file: pendingFile, courseId }),
            )) as string;
            await dispatchPromise(
                addSandboxClassAsset({
                    courseId,
                    body: {
                        name: name.trim(),
                        description: description.trim(),
                        file_key,
                        file_size_bytes: pendingFile.size,
                        original_filename: pendingFile.name,
                    },
                }),
            );
            showToastMessage('Asset uploaded', 'success');
            setPendingFile(null);
            setName('');
            setDescription('');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Upload failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (assetId: string) => {
        setBusy(true);
        try {
            await dispatchPromise(deleteSandboxClassAsset({ courseId, assetId }));
            showToastMessage('Asset removed', 'success');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not delete', 'error');
        } finally {
            setBusy(false);
        }
    };

    const saveMeta = async (asset: SandboxClassAsset, nextName: string, nextDesc: string) => {
        setBusy(true);
        try {
            await dispatchPromise(
                updateSandboxClassAsset({
                    courseId,
                    assetId: asset._id,
                    body: { name: nextName.trim(), description: nextDesc.trim() },
                }),
            );
            showToastMessage('Asset updated', 'success');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not update', 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    bgcolor: OBSIDIAN.surfaceContainer,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${OBSIDIAN.outline}33`,
                },
            }}>
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '80vh' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                fontSize: 18,
                                color: OBSIDIAN.onSurface,
                            }}>
                            Class assets
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, mt: 0.25 }}>
                            {courseTitle || 'Course'} · ZIP only · max 20MB each
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} disabled={busy} sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <input
                    ref={inputRef}
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    hidden
                    onChange={onPickFile}
                />

                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: OBSIDIAN.surfaceContainerHigh,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}>
                    <Button
                        startIcon={<CloudUploadOutlinedIcon />}
                        onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontWeight: 700,
                            color: OBSIDIAN.primary,
                            border: `1px dashed ${OBSIDIAN.primary}66`,
                            borderRadius: 2,
                        }}>
                        {pendingFile ? pendingFile.name : 'Choose ZIP'}
                    </Button>
                    {pendingFile ? (
                        <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant }}>
                            {formatSize(pendingFile.size)}
                        </Typography>
                    ) : null}
                    <TextField
                        label="Name"
                        size="small"
                        value={name}
                        onChange={e => setName(e.target.value.slice(0, 100))}
                        sx={fieldSx}
                    />
                    <TextField
                        label="Description"
                        size="small"
                        multiline
                        minRows={2}
                        value={description}
                        onChange={e => setDescription(e.target.value.slice(0, 500))}
                        sx={fieldSx}
                    />
                    <Button
                        disabled={busy || !pendingFile}
                        onClick={() => void upload()}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: OBSIDIAN.primary,
                            color: OBSIDIAN.onPrimaryFixed,
                            borderRadius: 999,
                            '&:hover': { filter: 'brightness(1.05)' },
                            '&.Mui-disabled': {
                                bgcolor: OBSIDIAN.surfaceContainerHighest,
                                color: OBSIDIAN.onSurfaceVariant,
                            },
                        }}>
                        {busy ? 'Uploading…' : 'Upload asset'}
                    </Button>
                </Box>

                <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 120 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} sx={{ color: OBSIDIAN.primary }} />
                        </Box>
                    ) : assets.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant, py: 2 }}>
                            No assets yet. Upload ZIPs students can download after enrollment.
                        </Typography>
                    ) : (
                        assets.map(asset => (
                            <AssetRow
                                key={asset._id}
                                asset={asset}
                                disabled={busy}
                                onDelete={() => void remove(asset._id)}
                                onSave={(n, d) => void saveMeta(asset, n, d)}
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}

function AssetRow({
    asset,
    disabled,
    onDelete,
    onSave,
}: {
    asset: SandboxClassAsset;
    disabled?: boolean;
    onDelete: () => void;
    onSave: (name: string, description: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(asset.name);
    const [description, setDescription] = useState(asset.description || '');

    useEffect(() => {
        setName(asset.name);
        setDescription(asset.description || '');
        setEditing(false);
    }, [asset._id, asset.name, asset.description]);

    return (
        <Box
            sx={{
                mb: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: OBSIDIAN.surfaceContainerHighest,
                display: 'flex',
                gap: 1.25,
                alignItems: 'flex-start',
            }}>
            <FolderZipOutlinedIcon sx={{ color: OBSIDIAN.primary, mt: 0.25 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {editing ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField
                            size="small"
                            value={name}
                            onChange={e => setName(e.target.value.slice(0, 100))}
                            sx={fieldSx}
                        />
                        <TextField
                            size="small"
                            multiline
                            minRows={2}
                            value={description}
                            onChange={e => setDescription(e.target.value.slice(0, 500))}
                            sx={fieldSx}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                disabled={disabled}
                                onClick={() => onSave(name, description)}
                                sx={{ textTransform: 'none', color: OBSIDIAN.primary, fontWeight: 700 }}>
                                Save
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setName(asset.name);
                                    setDescription(asset.description || '');
                                    setEditing(false);
                                }}
                                sx={{ textTransform: 'none', color: OBSIDIAN.onSurfaceVariant }}>
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: OBSIDIAN.onSurface }}>
                            {asset.name}
                        </Typography>
                        {asset.description ? (
                            <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, mt: 0.25 }}>
                                {asset.description}
                            </Typography>
                        ) : null}
                        <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant, mt: 0.5 }}>
                            {formatSize(asset.file_size_bytes)}
                            {asset.original_filename ? ` · ${asset.original_filename}` : ''}
                        </Typography>
                        <Button
                            size="small"
                            disabled={disabled}
                            onClick={() => setEditing(true)}
                            sx={{
                                mt: 0.5,
                                px: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                color: OBSIDIAN.primary,
                                fontWeight: 700,
                            }}>
                            Edit
                        </Button>
                    </>
                )}
            </Box>
            <IconButton size="small" disabled={disabled} onClick={onDelete} sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                <DeleteOutlineIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}
